/**
 * Pure, framework-free helpers behind {@link DeviceCapabilityService}.
 *
 * Kept separate from the Angular service so the decision logic can be unit
 * tested without a DOM or DI container. Nothing here reads global state except
 * {@link readDeviceSignals}, which is the single point that touches `navigator`.
 */

/** Hardware capability signals sampled from the browser. `null` = not reported. */
export interface DeviceSignals {
  /** `navigator.deviceMemory` in GB, or `null` when the browser doesn't expose it. */
  deviceMemory: number | null;
  /** `navigator.hardwareConcurrency` (logical cores), or `null`. */
  cpuCores: number | null;
  /** Whether the WebGPU backend is available (`'gpu' in navigator`). */
  webgpu: boolean;
}

/** Thresholds at/below which we treat a device as too weak for the heavy stack. */
export interface WeakThresholds {
  weakMemoryGb: number;
  weakCores: number;
}

export const DEFAULT_WEAK_THRESHOLDS: WeakThresholds = {
  weakMemoryGb: 2,
  weakCores: 2,
};

/** Sample the current device signals from `navigator` (SSR/test-safe). */
export function readDeviceSignals(): DeviceSignals {
  const nav: any = typeof navigator !== 'undefined' ? navigator : {};
  const mem = typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null;
  const cores = typeof nav.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : null;
  return {
    deviceMemory: mem,
    cpuCores: cores,
    webgpu: typeof nav === 'object' && 'gpu' in nav,
  };
}

/**
 * A device is "weak" when a *reported* signal is at or below its threshold.
 * Missing signals never flag a device — we don't punish browsers that decline
 * to report `deviceMemory` / `hardwareConcurrency`.
 */
export function isWeakDevice(
  s: DeviceSignals,
  th: WeakThresholds = DEFAULT_WEAK_THRESHOLDS,
): boolean {
  if (s.deviceMemory != null && s.deviceMemory <= th.weakMemoryGb) return true;
  if (s.cpuCores != null && s.cpuCores <= th.weakCores) return true;
  return false;
}

/** Result of racing a unit of work against a time budget. */
export type BudgetOutcome<T> =
  | { ok: true; value: T; elapsedMs: number }
  | { ok: false; kind: 'timeout' | 'error'; elapsedMs: number };

const TIMEOUT = Symbol('budget-timeout');

/**
 * Run `fn()` but give up after `ms`. Resolves (never rejects) to a
 * {@link BudgetOutcome}: the value if it finished in time, a `'timeout'` if it
 * overran, or an `'error'` if it rejected. A timeout cannot cancel in-flight
 * work (e.g. ONNX/wasm); we simply stop awaiting and let it finish in the
 * background. `clock` is injectable so elapsed time is testable.
 */
export async function withBudget<T>(
  fn: () => Promise<T>,
  ms: number,
  clock: () => number = () => performance.now(),
): Promise<BudgetOutcome<T>> {
  const start = clock();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<typeof TIMEOUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMEOUT), ms);
  });
  try {
    const raced = await Promise.race([fn(), timeout]);
    if (raced === TIMEOUT) {
      return { ok: false, kind: 'timeout', elapsedMs: clock() - start };
    }
    return { ok: true, value: raced as T, elapsedMs: clock() - start };
  } catch {
    return { ok: false, kind: 'error', elapsedMs: clock() - start };
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
